import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { ChevronRightIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

import type { AccordionItem } from '@/components/Accordion';
import { Accordion } from '@/components/Accordion';
import { CopyToClipboard } from '@/components/CopyToClipboard';
import { Checkbox } from '@/components/form/Checkbox';
import { InputText } from '@/components/form/InputText';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

const PROXY_URL_REGEX = /^(https?|socks5):\/\/[^\s/$.?#][^\s]*$/i;

/**
 * Validates a proxy URL against the supported schemes (http, https, socks5)
 * and ensures it contains a valid host.
 */
function validateProxyURL(value: string): string | undefined {
  if (!value) return undefined;
  if (!PROXY_URL_REGEX.test(value)) {
    return 'Invalid proxy URL. Use http://<host>[:<port>], https://<host>[:<port>], or socks5://<host>[:<port>]';
  }
  try {
    const parsed = new URL(value);
    if (!['http:', 'https:', 'socks5:'].includes(parsed.protocol)) {
      return 'Unsupported scheme. Use http, https, or socks5.';
    }
    if (!parsed.hostname) {
      return 'Proxy URL must include a host.';
    }
  } catch {
    return 'Invalid proxy URL format.';
  }
  return undefined;
}

interface DownloadAccordionProps {
  onDownloadInstaller: (
    type: 'msi' | 'deb' | 'rpm',
    proxyConfiguration?: string
  ) => Promise<void>;
}

/**
 * DownloadAccordion displays platform-specific installation instructions
 * for Windows, and Linux systems.
 *
 * Extracted from agents/index.tsx Phase 1.1 refactor (2025-10-22)
 *
 * Features:
 * - Platform-specific download buttons with loading states
 * - Installation commands with copy-to-clipboard
 * - System requirements for each platform
 * - Uninstall instructions
 * - Optional proxy configuration for corporate networks
 */
export const DownloadAccordion = ({
  onDownloadInstaller,
}: DownloadAccordionProps) => {
  const [downloadingPlatform, setDownloadingPlatform] = useState<string | null>(
    null
  );
  const [proxyEnabled, setProxyEnabled] = useState(false);
  const [proxyURL, setProxyURL] = useState('');

  const proxyError = useMemo(
    () => (proxyEnabled && proxyURL ? validateProxyURL(proxyURL) : undefined),
    [proxyEnabled, proxyURL]
  );

  const isProxyInvalid = proxyEnabled && (!proxyURL || !!proxyError);
  const proxyValue =
    proxyEnabled && proxyURL && !proxyError ? proxyURL : undefined;

  const handleDownload = async (type: 'msi' | 'deb' | 'rpm') => {
    setDownloadingPlatform(type);
    try {
      await onDownloadInstaller(type, proxyValue);
    } finally {
      setDownloadingPlatform(null);
    }
  };

  const agentDownloadAccordionItems: AccordionItem[] = [
    {
      id: 'windows',
      title: 'Microsoft Windows',
      subtitle: 'Initial Setup and Permissions',
      defaultOpen: true,
      content: (
        <div className="flex flex-col gap-8 md:flex-row">
          {/* Left column: Download, install path, commands */}
          <div className="flex min-w-[320px] flex-1 flex-col gap-5">
            <Button
              variant="outline"
              size="sm"
              className="w-fit rounded-full"
              onClick={() => handleDownload('msi')}
              disabled={downloadingPlatform !== null || isProxyInvalid}
            >
              {downloadingPlatform === 'msi'
                ? 'Generating MSI...'
                : 'Download MSI Installer'}
              {downloadingPlatform === 'msi' ? (
                <Spinner size="sm" />
              ) : (
                <ChevronRightIcon className="size-4" />
              )}
            </Button>
            <div className="space-y-1">
              <h3 className="text-md text-chariot-text-primary font-semibold">
                Installs to:
              </h3>
              <div className="flex items-center gap-2">
                <CopyToClipboard
                  textToCopy={'C:\\Program Files\\Praetorian Aegis\\'}
                  alwaysShowCopyIcon
                >
                  <code className="text-chariot-brand-primary mr-2 text-xs">
                    C:\Program Files\Praetorian Aegis\
                  </code>
                </CopyToClipboard>
              </div>
              <div className="text-chariot-text-secondary text-xs">
                Creates service named Praetorian Aegis Service
              </div>
            </div>
            <div className="flex flex-row gap-2">
              <CheckCircleIcon className="text-chariot-stroke-info size-6" />
              <div className="flex flex-col">
                <h3 className="text-chariot-text-primary font-bold">
                  Install Aegis (MSI)
                </h3>
                <div className="flex items-center gap-2">
                  <CopyToClipboard
                    textToCopy="msiexec /i praetorian_aegis.msi"
                    alwaysShowCopyIcon
                  >
                    <code className="text-chariot-brand-primary mr-2 text-sm">
                      msiexec /i praetorian_aegis.msi
                    </code>
                  </CopyToClipboard>
                </div>
              </div>
            </div>
            <div className="flex flex-row gap-2">
              <XCircleIcon className="text-chariot-stroke-critical size-6" />
              <div className="flex flex-col">
                <h3 className="text-chariot-text-primary font-bold">
                  Uninstall Aegis (MSI)
                </h3>
                <div className="flex items-center gap-2">
                  <CopyToClipboard
                    textToCopy="msiexec /x praetorian_aegis.msi"
                    alwaysShowCopyIcon
                  >
                    <code className="text-chariot-text-red mr-2 text-sm">
                      msiexec /x praetorian_aegis.msi
                    </code>
                  </CopyToClipboard>
                </div>
                <div className="text-chariot-text-secondary mt-2 text-xs">
                  Or use{' '}
                  <span className="font-semibold">Add/Remove Programs</span> in
                  the Control Panel
                </div>
              </div>
            </div>
          </div>
          {/* Right column: System Requirements card */}
          <div className="flex flex-1 justify-center md:justify-end">
            <div className="border-chariot-stroke bg-chariot-background-primary h-fit w-full max-w-xs rounded-md border p-6">
              <img
                src="/icons/microsoft.svg"
                className="text-chariot-brand-primary mb-2 size-8"
                alt="Windows"
              />
              <div className="text-chariot-text-primary mb-2 font-bold">
                System Requirements
              </div>
              <ul className="text-chariot-text-secondary list-inside list-disc space-y-1 text-sm">
                <li>Windows 8 / Server 2012+</li>
                <li>Domain joined</li>
                <li>16 GB RAM minimum</li>
                <li>150 GB free disk space</li>
                <li>Local admin rights required</li>
              </ul>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'linux',
      title: 'Linux',
      subtitle: 'Initial Setup and Permissions',
      content: (
        <div className="flex flex-col">
          {/* Top row: Download buttons */}
          <div className="mb-6 flex flex-row items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="w-fit rounded-full"
              onClick={() => handleDownload('rpm')}
              disabled={downloadingPlatform !== null || isProxyInvalid}
            >
              <img src="/icons/redhat.svg" className="size-4" alt="Red Hat" />
              {downloadingPlatform === 'rpm'
                ? 'Generating RPM...'
                : 'Download RPM Installer'}
              {downloadingPlatform === 'rpm' ? (
                <Spinner size="sm" />
              ) : (
                <ChevronRightIcon className="size-4" />
              )}
            </Button>
            <span className="text-chariot-text-secondary font-semibold">
              or
            </span>
            <Button
              variant="outline"
              size="sm"
              className="w-fit rounded-full"
              onClick={() => handleDownload('deb')}
              disabled={downloadingPlatform !== null || isProxyInvalid}
            >
              <img src="/icons/debian.svg" className="size-4" alt="Debian" />
              {downloadingPlatform === 'deb'
                ? 'Generating DEB...'
                : 'Download DEB Installer'}
              {downloadingPlatform === 'deb' ? (
                <Spinner size="sm" />
              ) : (
                <ChevronRightIcon className="size-4" />
              )}
            </Button>
          </div>
          <div className="flex flex-col gap-8 md:flex-row">
            {/* Left column: install/verify instructions */}
            <div className="flex w-full min-w-[320px] flex-1 flex-col gap-5">
              <div>
                <h3 className="text-chariot-text-primary mb-2 font-bold">
                  <CheckCircleIcon className="text-chariot-stroke-info mr-2 inline-block size-6" />
                  Install Aegis (PKG)
                </h3>
                <div className="mb-2">
                  <span className="text-chariot-text-primary font-semibold">
                    <img
                      src="/icons/redhat.svg"
                      className="mr-2 inline-block size-4"
                      alt="Red Hat"
                    />{' '}
                    RHEL-based:
                  </span>
                  <CopyToClipboard
                    textToCopy="sudo dnf install PraetorianAegisInstaller.rpm"
                    alwaysShowCopyIcon
                  >
                    <code className="text-chariot-brand-primary mr-2 text-sm">
                      sudo dnf install PraetorianAegisInstaller.rpm
                    </code>
                  </CopyToClipboard>
                  <CopyToClipboard
                    textToCopy="sudo systemctl start praetorian_client"
                    alwaysShowCopyIcon
                  >
                    <code className="text-chariot-brand-primary mr-2 text-sm">
                      sudo systemctl start praetorian_client
                    </code>
                  </CopyToClipboard>
                </div>
                <div className="mb-2">
                  <span className="text-chariot-text-primary font-semibold">
                    <img
                      src="/icons/debian.svg"
                      className="mr-2 inline-block size-4"
                      alt="Debian"
                    />{' '}
                    Debian-based:
                  </span>
                  <CopyToClipboard
                    textToCopy="sudo dpkg -i PraetorianAegisInstaller.deb"
                    alwaysShowCopyIcon
                  >
                    <code className="text-chariot-brand-primary mr-2 text-sm">
                      sudo dpkg -i PraetorianAegisInstaller.deb
                    </code>
                  </CopyToClipboard>
                </div>
              </div>
              <div>
                <h3 className="text-chariot-text-primary mb-2 font-bold">
                  Verify Installation
                </h3>
                <div className="mb-2">
                  <span className="text-chariot-text-secondary mr-2 font-semibold">
                    SysVinit:
                  </span>
                  <CopyToClipboard
                    textToCopy="systemctl status praetorian_client"
                    alwaysShowCopyIcon
                  >
                    <code className="text-chariot-brand-primary mr-2 text-sm">
                      systemctl status praetorian_client
                    </code>
                  </CopyToClipboard>
                </div>
                <div className="mb-2">
                  <span className="text-chariot-text-secondary mr-2 font-semibold">
                    Systemd:
                  </span>
                  <CopyToClipboard
                    textToCopy="systemctl status praetorian_client"
                    alwaysShowCopyIcon
                  >
                    <code className="text-chariot-brand-primary mr-2 text-sm">
                      systemctl status praetorian_client
                    </code>
                  </CopyToClipboard>
                </div>
              </div>
            </div>
            {/* Right column: System Requirements card */}
            <div className="flex flex-1 justify-center md:justify-end">
              <div className="border-chariot-stroke bg-chariot-background-primary h-fit w-full max-w-xs rounded-md border p-6">
                <img
                  src="/icons/tux.svg"
                  className="text-chariot-brand-primary mb-2 size-8"
                  alt="Linux"
                />
                <div className="text-chariot-text-primary mb-2 font-bold">
                  System Requirements
                </div>
                <ul className="text-chariot-text-secondary list-inside list-disc space-y-1 text-sm">
                  <li>Ubuntu 20.04+ or RHEL 8+</li>
                  <li>Root or sudo access</li>
                  <li>6 GB RAM (min)</li>
                  <li>60 GB free disk space</li>
                </ul>
              </div>
            </div>
          </div>
          {/* Divider */}
          <div className="border-chariot-stroke my-4 h-px w-full border" />
          {/* Uninstall section below */}
          <div>
            <h3 className="text-chariot-text-primary mb-2 font-bold">
              <XCircleIcon className="text-chariot-stroke-critical mr-2 inline-block size-6" />
              Uninstall Aegis
            </h3>
            <div className="flex flex-row gap-20">
              <div className="flex-start text-chariot-text-red mb-2 flex flex-col gap-2">
                <p className="text-chariot-text-primary font-semibold">
                  <img
                    src="/icons/redhat.svg"
                    className="inline-block size-4"
                    alt="Red Hat"
                  />{' '}
                  RHEL:
                </p>
                <div>
                  <CopyToClipboard
                    textToCopy="sudo systemctl stop praetorian_client"
                    alwaysShowCopyIcon
                  >
                    <code className="mr-2 text-sm">
                      sudo systemctl stop praetorian_client
                    </code>
                  </CopyToClipboard>
                  <CopyToClipboard
                    textToCopy="sudo dnf remove praetorianAegis_client"
                    alwaysShowCopyIcon
                  >
                    <code className="mr-2 text-sm">
                      sudo dnf remove praetorianAegis_client
                    </code>
                  </CopyToClipboard>
                </div>
              </div>
              <div className="flex-start text-chariot-text-red mb-2 flex flex-col gap-2">
                <p className="text-chariot-text-primary font-semibold">
                  <img
                    src="/icons/debian.svg"
                    className="inline-block size-4"
                    alt="Debian"
                  />{' '}
                  Debian:
                </p>
                <div>
                  <CopyToClipboard
                    textToCopy="sudo dpkg -r praetorian-aegis"
                    alwaysShowCopyIcon
                  >
                    <code className="mr-2 text-sm">
                      sudo dpkg -r praetorian-aegis
                    </code>
                  </CopyToClipboard>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="border-chariot-stroke rounded-md border p-4">
        <Checkbox
          name="proxyEnabled"
          label="Configure Proxy"
          value={proxyEnabled ? 'true' : 'false'}
          onChange={() => {
            setProxyEnabled(prev => !prev);
            if (proxyEnabled) setProxyURL('');
          }}
        />
        {proxyEnabled && (
          <div className="mt-3 max-w-md">
            <InputText
              name="proxyURL"
              placeholder="http://proxy.corp.local:8080"
              value={proxyURL}
              error={proxyError}
              onChange={e => setProxyURL(e.target.value)}
            />
            {proxyError && (
              <p className="text-chariot-text-red mt-1 text-xs">{proxyError}</p>
            )}
          </div>
        )}
      </div>
      <Accordion items={agentDownloadAccordionItems} className="w-full" />
    </div>
  );
};
